import { useEffect, useRef, useState } from "react";
import { Card, Form } from "react-bootstrap";
import SignaturePad from "react-signature-canvas";
import axios from "axios";
import { toast } from "react-toastify";
import { Button } from "../Button/Button";
import HelperService from "../utility/HelperService";

interface propData {
    tenantUrl?: string;
    accessToken?: string;
    companyCode: any;
    quoteNum: any;
    quoteTagOrder: any;
    onBack: () => void;
    onApproved?: () => void;
    termsAndConditions?: string;
    onApprove: () => void;
}

const formatAmount = (value: any) => `$${HelperService.getCurrencyFormatter(Number(value) || 0)}`;

const Section = ({ title, color, defaultOpen, collapsible = true, children }: { title: string; color: string; defaultOpen?: boolean; collapsible?: boolean; children?: any }) => {
    const [open, setOpen] = useState(!!defaultOpen);
    return (
        <div>
            <div className="px-3 py-3 fw-bold border-top" style={{ backgroundColor: color, cursor: collapsible ? 'pointer' : 'default' }} onClick={collapsible ? () => setOpen(!open) : undefined}>{title}</div>
            {(!collapsible || open) && <div className="px-3 py-2">{children}</div>}
        </div>
    );
};

const SalesQuoteSelectedOption = (props: propData) => {
    const [summary, setSummary] = useState<any>(null);
    const [optionalSelection, setOptionalSelection] = useState<Record<number, boolean>>({});
    const [accepted, setAccepted] = useState(false);
    const [signedBy, setSignedBy] = useState("");
    const [isSaving, setSaving] = useState(false);
    const sigPadRef = useRef<SignaturePad>(null);

    useEffect(() => {
        axios.get(`${props.tenantUrl}/api/SalesQuote/GetSalesQuoteSummary`, {
            params: { companyCode: props.companyCode, quotenum: props.quoteNum, quoteTagOrder: props.quoteTagOrder },
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + props.accessToken },
        }).then((res) => {
            const responseData = res.data?.data ?? null;
            setSummary(responseData);
            const initialSelection: Record<number, boolean> = {};
            (responseData?.optionalItems ?? []).forEach((item: any, index: number) => {
                if (item.itemAccepted === 'Y') initialSelection[index] = true;
                if (item.itemAccepted === 'N') initialSelection[index] = false;
            });
            setOptionalSelection(initialSelection);
            const name = `${responseData?.firstName ?? ''} ${responseData?.lastName ?? ''}`.trim();
            if (name) setSignedBy(name);
        }).catch((e) => {
            const data = e?.response?.data;
            toast.error((typeof data === "string" ? data : data?.message) || e?.message || "Failed to load option details");
        });
    }, [props.quoteTagOrder]);

    const pricing = summary?.pricingTypes?.find((p: any) => String(p.tagNum) === String(props.quoteTagOrder)) ?? summary?.pricingTypes?.[0];
    const items: any[] = summary?.itemDetails ?? [];
    const equipments = items.filter((i) => i.isMainEquipment && i.showItemToCustomer === true);
    const addons = items.filter((i) => (i.isAddon || i.categoryNameId === 'Miscellaneous') && i.showItemToCustomer === true);
    const pricingItems = items.filter((i) => !i.isAddon && !i.isMainEquipment && i.categoryNameId !== 'Miscellaneous' && i.showItemToCustomer === true);
    const coupons: any[] = summary?.coupons ?? [];
    const optionalItems: any[] = summary?.optionalItems ?? [];
    const headerColor = pricing?.headerColor;
    const customerName = `${summary?.firstName ?? ''} ${summary?.lastName ?? ''}`.trim();

    const onAccept = async () => {
        if (isSaving) return;
        if (sigPadRef.current?.isEmpty()) {
            toast.error("Please provide a signature first");
            return;
        }
        if (!signedBy.trim()) {
            toast.error("Please enter Signed By");
            return;
        }
        const base64 = sigPadRef.current!.toDataURL().replace("data:image/png;base64,", "");
        const payload = {
            companycode: String(props.companyCode),
            quotenum: Number(summary?.quoteNum ?? props.quoteNum),
            quotetagorder: String(props.quoteTagOrder),
            signedby: signedBy.trim(),
            signature: base64,
            accepted: "Y",
            downpayment: Number(summary?.downPayment ?? pricing?.paymentAmount ?? 0),
            paymenttype: pricing?.paymentType ?? "",
            monthlypayment: Number(pricing?.monthlyPayment ?? 0),
            discountcriteria: summary?.discountCriteria ?? "",
            discountperamount: Number(summary?.discountAmtPerc ?? 0),
            additionaldiscountamt: Number(summary?.additionalDiscountAmt ?? 0),
        };
        setSaving(true);
        const config = {
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + props.accessToken },
        };
        try {
            for (let index = 0; index < optionalItems.length; index += 1) {
                const item = optionalItems[index];
                const result = await axios.post(`${props.tenantUrl}/api/SalesQuote/UpdateOptionalItem`, {
                    companyCode: String(props.companyCode),
                    quotenum: String(summary?.quoteNum ?? props.quoteNum),
                    sortId: item.sortId,
                    itemAccepted: optionalSelection[index],
                }, config);
                if (result.data?.success === false) {
                    toast.error(result.data?.errorInfo?.Message || result.data?.message || "Error updating optional item");
                    return;
                }
            }
            const res = await axios.post(`${props.tenantUrl}/api/SalesQuote/AcceptUpdateQuote`, payload, config);
            if (res.data?.success) {
                toast.success("Quote Approved Successfully");
                props.onApproved?.();
            } else {
                toast.error(res.data?.errorInfo?.Message || res.data?.message || "Error approving quote");
            }
        } catch (error: any) {
            toast.error(error?.response?.data?.errorInfo?.Message || error?.response?.data?.message || "Error saving quote");
        } finally {
            setSaving(false);
        }
    };

    const allOptionalAnswered = optionalItems.every((_, index) => optionalSelection[index] !== undefined);
    const sectionColor = '#f5d5ad';

    return (
        <div className="p-2" style={{ backgroundColor: "#E0E0E0", minHeight: '100%' }}>
            <div className="d-flex m-1 mb-2">
                <Button label="Back" size="medium" onClick={props.onBack} />
            </div>

            {summary && (
                <>
                    <div style={{ position: 'sticky', top: 0, zIndex: 2, border: `1px solid ${headerColor}`, borderRadius: '6px 6px 0 0', overflow: 'hidden' }}>
                        <div className="px-2 py-2 fs-4 text-white" style={{ backgroundColor: headerColor }}>{formatAmount(pricing?.balance)}</div>
                        <div className="d-flex justify-content-between p-3" style={{ backgroundColor: '#f7f7f7' }}>
                            <div>
                                <div>Sales price {formatAmount(pricing?.tagPrice)}</div>
                                <div>Optional Items {formatAmount(pricing?.tagOptionItemPrice)}</div>
                            </div>
                            <div>
                                <div>Add-ons {formatAmount(pricing?.tagAddOn)}</div>
                                <div>Downpayment {formatAmount(summary?.downPayment ?? pricing?.paymentAmount)}</div>
                            </div>
                            <div>
                                <div>Coupons {formatAmount(pricing?.tagCoupon)}</div>
                                <div>Balance {formatAmount(pricing?.balance)}</div>
                            </div>
                        </div>
                    </div>

                    <Card className="p-0 overflow-hidden mb-2" style={{ border: `1px solid ${headerColor}`, borderTop: 0, borderRadius: '0 0 6px 6px' }}>

                        <Section title="Equipment" color="#fff" defaultOpen>
                            {equipments.map((item, index) => (
                                <div key={`${item.partNum}-${index}`} className="border-bottom py-2">
                                    <div className="d-flex">
                                        <div className="me-3 flex-shrink-0" style={{ width: 160 }}>
                                            {item.imageUrl && <img src={item.imageUrl} style={{ maxWidth: '100%', maxHeight: 130, objectFit: 'contain' }} />}
                                        </div>
                                        <div className="flex-grow-1 d-flex justify-content-between">
                                            <div>
                                                <div className="fw-bold">{item.partNum}</div>
                                                <div>{item.partDescription}</div>
                                            </div>
                                            <div><span style={{ color: '#e8681a' }}>Manufacturer</span> {item.partManufacturer}</div>
                                            <div><span style={{ color: '#e8681a' }}>Model</span> {item.partModel}</div>
                                        </div>
                                    </div>
                                    <div className="fw-bold mt-3">Features</div>
                                    {item.featureList && <div className="mt-2">{item.featureList}</div>}
                                </div>
                            ))}
                        </Section>

                        <Section title="Pricing Items" color={sectionColor} collapsible={false}>
                            {pricingItems.map((item, index) => (
                                <div className="py-2 border-bottom" key={`${item.partNum}-${index}`}>
                                    <div className="d-flex justify-content-between" style={{ color: '#e8681a' }}>
                                        <span>{item.categoryNameId}</span>
                                        <span>{Number(item.quantityHrs).toFixed(2)} <span>Hrs</span></span>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span>{item.r_o_d === 'R' ? 'Regular Time' : item.partDescription}</span>
                                        <span>
                                            {item.trade && <span className="me-2"><span style={{ color: '#e8681a' }}>Trade</span> {item.trade}</span>}
                                            {item.level && <span><span style={{ color: '#e8681a' }}>Level</span> {item.level}</span>}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </Section>

                        <Section title="Add-ons" color={sectionColor} collapsible={false}>
                            {addons.map((item, index) => (
                                <div className="py-2 border-bottom" key={`${item.partNum}-${index}`}>
                                    <div className="d-flex justify-content-between" style={{ color: '#e8681a' }}>
                                        <span>{item.categoryNameId}</span>
                                        <span>{Number(item.quantityHrs).toFixed(2)}</span>
                                    </div>
                                    <div>{item.categoryNameId === 'Miscellaneous' ? item.categoryNameId : item.partDescription}</div>
                                </div>
                            ))}
                        </Section>

                        <Section title="Coupons" color={sectionColor} defaultOpen>
                            <div className="d-flex flex-wrap">
                                {coupons.map((item, index) => (
                                    <div key={index} className="d-flex border me-2 mb-2" style={{ minWidth: 300 }}>
                                        <div style={{ width: 80, backgroundColor: '#ffc107' }} />
                                        <div className="p-2 mt-3">
                                            <div style={{ fontSize: '0.85rem' }}>{item.couponDescription}</div>
                                            <div className="fw-bold">{formatAmount(item.couponAmount)}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Section>
                    </Card>

                    {optionalItems.length > 0 && (
                        <Card className="p-0 overflow-hidden mb-2">
                            <div className="px-3 py-2" style={{ backgroundColor: '#fbe0d0' }}>[Step 1] Optional Items</div>
                            {optionalItems.map((item, index) => {
                                const selected = optionalSelection[index];
                                return (
                                    <div className="d-flex justify-content-between align-items-center px-4 py-2 border-bottom" key={index}>
                                        <span className="flex-fill">{item.optionalItemDescription}</span>
                                        <span className="me-5">{formatAmount(item.itemsSalesPrice)}</span>
                                        <div className="btn-group">
                                            <button className={`btn btn-sm ${selected === true ? 'btn-primary' : 'btn-outline-primary'}`} disabled={isSaving} onClick={() => setOptionalSelection({ ...optionalSelection, [index]: true })}>Yes</button>
                                            <button className={`btn btn-sm ${selected === false ? 'btn-primary' : 'btn-outline-primary'}`} disabled={isSaving} onClick={() => setOptionalSelection({ ...optionalSelection, [index]: false })}>No</button>
                                        </div>
                                    </div>
                                );
                            })}
                        </Card>
                    )}

                    {allOptionalAnswered && (
                        <Card className="p-0 overflow-hidden mb-2">
                            <div className="px-3 py-3" style={{ backgroundColor: '#fbe0d0' }}>[Step 2] Terms and Conditions</div>
                            <div className="p-3">
                                <Form.Control as="textarea" readOnly rows={10} value={props.termsAndConditions ?? ''} />
                                <Form.Check
                                    type="switch"
                                    id="accept-terms"
                                    className="mt-2 fw-bold"
                                    style={{ color: '#e8681a' }}
                                    label="Accept Terms and Conditions"
                                    checked={accepted}
                                    onChange={(e) => setAccepted(e.target.checked)}
                                />
                            </div>
                        </Card>
                    )}

                    {allOptionalAnswered && accepted && (
                        <Card className="p-0 overflow-hidden mb-2">
                            <div className="px-3 py-3" style={{ backgroundColor: '#fbe0d0' }}>[Step 3] Signature</div>
                            <div className="p-3">
                                <div className="mb-1">
                                    <Button label="Clear" size="small" onClick={() => sigPadRef.current?.clear()} />
                                </div>
                                <div style={{ display: 'inline-block', border: '1px solid #000' }}>
                                    <SignaturePad
                                        ref={sigPadRef}
                                        canvasProps={{ width: 525, height: 300, style: { backgroundColor: 'white' } }}
                                    />
                                </div>
                                <div className="d-flex align-items-center mt-2">
                                    <span className="fw-bold me-5">Signed By</span>
                                    <Form.Control style={{ width: 284 }} value={signedBy} onChange={(e) => setSignedBy(e.target.value)} />
                                    <span className="mx-1">&lt;-</span>
                                    <span className="ms-3" style={{ color: '#e8681a', cursor: 'pointer' }} onClick={() => setSignedBy(customerName)}>{customerName}</span>
                                </div>
                                <div className="text-end mt-3">
                                    <button className="btn btn-success btn-lg" disabled={isSaving} onClick={onAccept}>Accept</button>
                                    <div className="mt-2">Note: No changes can be made after Quote is Signed</div>
                                </div>
                            </div>
                        </Card>
                    )}
                </>
            )}
        </div>
    );
};

export default SalesQuoteSelectedOption;
